import { ChangeDetectionStrategy, Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take, takeUntil, catchError } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';

import { Feature } from 'src/app/features/feature-management/models';
import { FeatureManagementService } from 'src/app/features/feature-management/services';
import { PackagesSelectors } from 'src/app/features/packages/store/selectors';
import { MOBILE_BREAKPOINT_QUERY } from 'src/app/features/shared/constants';
import { HelpFaqActions } from '../../store/actions';
import { HelpFaqSelectors } from '../../store/selectors';
import { getHasAssociatedUsersAndPackage } from '../../store/selectors/help-faq.selectors';
import { ChatConversationStore } from './chat-conversation.store';

const ALLOWED_ROUTE_SEGMENTS = ['waiting-room', 'signing-room'];

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ChatConversationStore],
  standalone: false,
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  protected readonly store: ChatConversationStore;
  protected isEnabled = false;
  protected isMobile = false;
  protected isAllowedRoute = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    store: ChatConversationStore,
    private readonly ngrxStore: Store,
    private readonly featureManagementService: FeatureManagementService,
    private readonly injector: Injector,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly router: Router
  ) {
    this.store = store;
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe(MOBILE_BREAKPOINT_QUERY)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.router.url),
        startWith(this.router.url),
        map((url) => ALLOWED_ROUTE_SEGMENTS.some((segment) => url.includes(segment))),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((isAllowedRoute) => {
        this.isAllowedRoute = isAllowedRoute;
      });

    // Gate on the same condition as session-handoff-page's hasAssociatedUsers:
    // fires only when a participant has been associated, guaranteeing auth is complete.
    this.ngrxStore.pipe(
      select(getHasAssociatedUsersAndPackage),
      filter((has) => has === true),
      take(1),
      takeUntil(this.destroy$),
      switchMap(() => {
        const cached = this.featureManagementService.getIsFeatureEnabledWithCaching(Feature.HelpAssistWidget);
        return cached !== undefined
          ? of(cached)
          : this.featureManagementService.getIsFeatureFlagEnabled(Feature.HelpAssistWidget).pipe(
              catchError(() => of(false)),
            );
      })
    ).subscribe((isEnabled) => {
      this.isEnabled = isEnabled;
      if (isEnabled) {
        this.watchWidgetState();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.document.body.classList.remove('faq-open');
    this.document.querySelector('[inert]')?.removeAttribute('inert');
  }

  private watchWidgetState(): void {
    toObservable(this.store.widgetState, { injector: this.injector }).pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((state) => {
      if (state === 'opened') {
        this.document.querySelector('app-root > :not(app-chat-widget)')?.setAttribute('inert', '');
        this.document.body.classList.add('faq-open');
        this.loadFaqsIfNeeded();
      } else {
        this.document.querySelector('[inert]')?.removeAttribute('inert');
        this.document.body.classList.remove('faq-open');
      }
    });
  }

  private loadFaqsIfNeeded(): void {
    this.ngrxStore
      .pipe(select(HelpFaqSelectors.getIsFaqLoaded), take(1))
      .subscribe((isLoaded) => {
        if (!isLoaded) {
          this.ngrxStore.select(PackagesSelectors.getActivePackageGuid).pipe(
            filter((guid) => !!guid),
            take(1)
          ).subscribe(() => {
            this.ngrxStore.dispatch(HelpFaqActions.LoadFaqs());
          });
        }
      });
  }
}
